import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getAcceptTermsText, ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import InputField from 'Components/InputField';
import PasswordField from 'Components/PasswordField';
import ProgressBar from 'Components/ProgressBar';
import TermsPanel from 'Components/TermsPanel';
const { EVENTS } = analytics;

const Register = (props) => {
  const [submitting, setSubmitting] = useState(false);
  const [validForm, setValidForm] = useState(false);
  const [validConsent, setValidConsent] = useState(false);
  const [registrationItems, setRegistrationItems] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');

  const fetchPolicies = async () => {
    const registrationItems = await props.uim.fetchRegistrationItems(
      props.country
    );
    setRegistrationItems(registrationItems);
  };

  const isValidPassword = (pw) => props.uim.passwordRegExp.test(pw);

  const isValidField = (value, event) => {
    const inputName = event.target.name;
    const isRequiredMsg = props.strings.isRequired.formatUimTranslation(
      props.strings[inputName]
    );
    switch (inputName) {
      case 'password': {
        const valid = isValidPassword(value);
        const invalidMsg = !!value.length
          ? props.strings.passwordLength
          : isRequiredMsg;
        const message = valid ? '' : invalidMsg;
        return {
          valid,
          message,
        };
      }
      default: {
        const valid = !!value.trim().length;
        const message = valid ? '' : isRequiredMsg;
        return {
          valid,
          message,
        };
      }
    }
  };

  const isValidForm = () => {
    // Determine if the form is complete; valid password and name
    const validPassword = isValidPassword(password);
    if (validPassword && firstName.length >= 1 && lastName.length >= 1) {
      // Need a place to display error message for consents
      return setValidForm(validConsent);
    }

    setValidForm(false);
  };

  const setConsentValidity = (valid, registrationItems) => {
    setValidConsent(valid);
    setRegistrationItems(registrationItems);
  };

  const handleChange = (event, attribute) => {
    const updateMethods = {
      firstName: setFirstName,
      lastName: setLastName,
      password: setPassword,
    };

    updateMethods[attribute](event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    analytics.event({ action: EVENTS.submit, label: 'signup' });
    setSubmitting(true);
    const selectedItems = props.uim.getSelectedRegistrationItems(
      registrationItems
    );
    const user = {
      firstName,
      lastName,
      pw: password,
      email: props.email,
      policies: selectedItems.policies,
      newsletters: selectedItems.newsletters,
      country: props.country.toUpperCase(),
      language: props.language,
      locale: props.locale,
    };
    props.registerUser(user);
  };

  useEffect(() => {
    if (props.isUimLoaded && props.email) fetchPolicies();
  }, [props.isUimLoaded]);

  useEffect(() => {
    if (props.isUimLoaded) isValidForm();
  }, [firstName, lastName, password, validConsent]);

  if (!props.isUimLoaded) return <ProgressBar />;

  // Don't immediately redirect when email doesn't exist because this page is the landing page for reverting registration
  // We want to redirect if user lands on this page or does a hard refresh and the email is not in memory from the previous screen
  if (!props.email) props.transitionTo(ROUTES.PAGE_HOME.url);

  return (
    <div className="register">
      {submitting && <ProgressBar />}
      <h1 className="page-heading">{props.email}</h1>
      <form
        onSubmit={handleSubmit}
        className="email-form"
        action={ROUTES.PAGE_REGISTER.url}
      >
        <div className="column--double">
          <InputField
            autoFocus
            disabled={submitting}
            id="firstName"
            isValid={isValidField}
            label={props.strings.firstName}
            name="firstName"
            onChange={(event) => handleChange(event, 'firstName')}
          />
          <InputField
            disabled={submitting}
            id="lastName"
            isValid={isValidField}
            label={props.strings.lastName}
            name="lastName"
            onChange={(event) => handleChange(event, 'lastName')}
          />
        </div>
        <PasswordField
          className="password-input"
          disabled={submitting}
          hideText={props.strings.hide}
          id="password"
          isValid={isValidField}
          label={props.strings.createPassword}
          name="password"
          onChange={(event) => handleChange(event, 'password')}
          showText={props.strings.show}
        />
        {registrationItems && (
          <TermsPanel
            disabled={submitting}
            locale={props.locale}
            registrationItems={registrationItems}
            setConsentValidity={setConsentValidity}
            uim={props.uim}
          />
        )}
        <button type="submit" disabled={!validForm || submitting}>
          {getAcceptTermsText(props.strings, props.country)}
        </button>
      </form>
    </div>
  );
};

Register.propTypes = {
  country: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  registerUser: PropTypes.func.isRequired,
  uim: PropTypes.object,
};

export default Register;
